import { FC, useCallback, useEffect, useState } from 'react';
import { Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ContainerOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { CommandButton } from 'components/ui/composed/dashboard/DashboardTable';
import useItemFieldTemplates from 'hooks/useItemFieldTemplates';
import { ItemFieldTemplateRow } from 'store/item-field-templates/itemFieldTemplate';
import { colors } from 'theme';
import {
    ITEM_FIELD_TEMPLATES_COUNTRY,
    ITEM_FIELD_TEMPLATES_FILTERS,
    ITEM_FIELD_TEMPLATES_FORM_TYPE,
    ITEM_FIELD_TEMPLATES_INTERNAL_TYPE,
} from '../constants';
import ArchiveItemFieldTemplateModal, { ArchiveAction } from './ArchiveItemFieldTemplateModal';
import DeleteItemFieldTemplateModal from './DeleteItemFieldTemplateModal';
import DuplicateItemFieldTemplateModal from './DuplicateItemFieldTemplateModal';
import toItemFieldTemplateRow from './toItemFieldTemplateRow';

export const itemFieldTemplatePath = (id?: string) =>
    `/customs-declarations/${ITEM_FIELD_TEMPLATES_COUNTRY}/${ITEM_FIELD_TEMPLATES_INTERNAL_TYPE}` +
    `/item-field-templates/${ITEM_FIELD_TEMPLATES_FORM_TYPE}/${id ?? 'new'}`;

const DEFAULT_PAGE_SIZE = 10;

type PendingArchive = {
    template: ItemFieldTemplateRow;
    action: ArchiveAction;
};

interface Props {
    archived: boolean;
    search: string;
}

const ItemFieldTemplatesTable: FC<Props> = ({ archived, search }) => {
    const navigate = useNavigate();
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pendingArchive, setPendingArchive] = useState<PendingArchive | null>(null);
    const [templateToDuplicate, setTemplateToDuplicate] = useState<ItemFieldTemplateRow | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<ItemFieldTemplateRow | null>(null);

    useEffect(() => {
        setPageNumber(0);
    }, [search, archived]);

    const {
        templates,
        isLoading,
        listTemplates,
        archiveTemplate,
        unarchiveTemplate,
        deleteTemplate,
        duplicateTemplate,
    } = useItemFieldTemplates();

    const reloadTemplates = useCallback(
        () => listTemplates({ ...ITEM_FIELD_TEMPLATES_FILTERS, pageNumber, pageSize, query: search, archived }),
        [listTemplates, pageNumber, pageSize, search, archived]
    );

    useEffect(() => {
        reloadTemplates();
    }, [reloadTemplates]);

    const handleConfirmDuplicate = async (name: string) => {
        if (!templateToDuplicate) return;

        const { id } = templateToDuplicate;
        setTemplateToDuplicate(null);

        await duplicateTemplate(id, name);
        reloadTemplates();
    };

    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;

        const { id } = templateToDelete;
        setTemplateToDelete(null);

        await deleteTemplate(id);
        reloadTemplates();
    };

    const handleConfirmArchive = async () => {
        if (!pendingArchive) return;

        const { template, action } = pendingArchive;
        setPendingArchive(null);

        if (action === 'archive') await archiveTemplate(template.id);
        else await unarchiveTemplate(template.id);

        reloadTemplates();
    };

    const archiveAction: ArchiveAction = archived ? 'unarchive' : 'archive';

    const columns: ColumnsType<ItemFieldTemplateRow> = [
        { title: 'Template Name', key: 'name', render: (_, record) => record.name },
        { title: 'Declaration Type', key: 'declarationType', render: (_, record) => record.declarationType },
        { title: 'Fields', key: 'fields', ellipsis: true, render: (_, record) => record.fieldsIncluded.join(', ') },
        { title: 'Codes', key: 'codes', ellipsis: true, render: (_, record) => record.codes.join(', ') },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 170,
            render: (_, record) => (
                <Space size={8}>
                    {!archived && (
                        <CommandButton
                            tooltip="Duplicate"
                            icon={<CopyOutlined style={{ color: colors.brand6 }} />}
                            onClick={(event) => {
                                event.stopPropagation();
                                setTemplateToDuplicate(record);
                            }}
                        />
                    )}
                    <CommandButton
                        tooltip={archived ? 'Unarchive' : 'Archive'}
                        icon={<ContainerOutlined style={{ color: colors.brand6 }} />}
                        onClick={(event) => {
                            event.stopPropagation();
                            setPendingArchive({ template: record, action: archiveAction });
                        }}
                    />
                    {archived && (
                        <CommandButton
                            tooltip="Delete"
                            icon={<DeleteOutlined style={{ color: colors.brand6 }} />}
                            onClick={(event) => {
                                event.stopPropagation();
                                setTemplateToDelete(record);
                            }}
                        />
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <StyledTableWrapper>
                <Table<ItemFieldTemplateRow>
                    rowKey="id"
                    columns={columns}
                    loading={isLoading}
                    dataSource={templates?.list.map(toItemFieldTemplateRow)}
                    pagination={{
                        current: (templates?.pageNumber || 0) + 1,
                        total: templates?.total,
                        pageSize: templates?.pageSize,
                        position: ['bottomCenter'],
                        hideOnSinglePage: true,
                    }}
                    onChange={({ current = 1, pageSize: newPageSize = pageSize }) => {
                        setPageNumber(current - 1);
                        setPageSize(newPageSize);
                    }}
                    onRow={(record) => ({ onClick: () => navigate(itemFieldTemplatePath(record.id)) })}
                    components={{
                        header: {
                            cell: (props: any) => <StyledTableHeaderCell {...props} />,
                        },
                    }}
                />
            </StyledTableWrapper>

            <ArchiveItemFieldTemplateModal
                open={pendingArchive !== null}
                action={pendingArchive?.action ?? archiveAction}
                templateName={pendingArchive?.template.name}
                onCancel={() => setPendingArchive(null)}
                onConfirm={handleConfirmArchive}
            />

            <DuplicateItemFieldTemplateModal
                open={templateToDuplicate !== null}
                templateName={templateToDuplicate?.name}
                onCancel={() => setTemplateToDuplicate(null)}
                onConfirm={handleConfirmDuplicate}
            />

            <DeleteItemFieldTemplateModal
                open={templateToDelete !== null}
                templateName={templateToDelete?.name}
                onCancel={() => setTemplateToDelete(null)}
                onConfirm={handleConfirmDelete}
            />
        </>
    );
};

export default ItemFieldTemplatesTable;
